/**
 * Explosion - Expanding/holding/shrinking explosion effect
 * Used for both player detonations and enemy missile destruction.
 * Lifecycle: expand -> hold -> shrink -> done
 * Includes shockwave ring that expands outward and fades.
 */

export class Explosion {
  constructor(x, y, maxRadius = 40) {
    this.x = x;
    this.y = y;
    this.maxRadius = maxRadius;
    this.radius = 0;
    this.phase = 'expand'; // 'expand', 'hold', 'shrink'
    this.alive = true;

    // Timing
    this.expandTime = 0.3;
    this.holdTime = 0.5;
    this.shrinkTime = 0.3;
    this.timer = 0;
    this.totalTime = 0; // total elapsed time since creation

    // Shockwave ring
    this.shockwaveRadius = 0;
    this.shockwaveMaxRadius = maxRadius * 2.2;
    this.shockwaveDuration = 0.6;
    this.shockwaveTimer = 0;
    this.shockwaveActive = true;

    // Visual
    this.particles = [];
  }

  update(dt) {
    if (!this.alive) return;

    this.timer += dt;
    this.totalTime += dt;

    // Update shockwave ring (independent of explosion phases)
    if (this.shockwaveActive) {
      this.shockwaveTimer += dt;
      const t = this.shockwaveTimer / this.shockwaveDuration;
      this.shockwaveRadius = t * this.shockwaveMaxRadius;
      if (this.shockwaveTimer >= this.shockwaveDuration) {
        this.shockwaveActive = false;
      }
    }

    switch (this.phase) {
      case 'expand':
        this.radius = (this.timer / this.expandTime) * this.maxRadius;
        if (this.timer >= this.expandTime) {
          this.radius = this.maxRadius;
          this.phase = 'hold';
          this.timer = 0;
        }
        break;

      case 'hold':
        this.radius = this.maxRadius;
        if (this.timer >= this.holdTime) {
          this.phase = 'shrink';
          this.timer = 0;
        }
        break;

      case 'shrink':
        this.radius = (1 - this.timer / this.shrinkTime) * this.maxRadius;
        if (this.timer >= this.shrinkTime) {
          this.radius = 0;
          this.alive = false;
        }
        break;
    }
  }

  render(ctx) {
    if (!this.alive || this.radius <= 0) return;

    ctx.save();

    // Shockwave ring - expanding circle that fades out
    if (this.shockwaveActive && this.shockwaveRadius > 0) {
      const shockT = this.shockwaveTimer / this.shockwaveDuration;
      const shockAlpha = (1 - shockT) * 0.6;
      const ringWidth = Math.max(1, (1 - shockT) * 3);

      ctx.save();
      ctx.strokeStyle = `rgba(255, 200, 100, ${shockAlpha})`;
      ctx.shadowColor = '#ffaa00';
      ctx.shadowBlur = 8 * (1 - shockT);
      ctx.lineWidth = ringWidth;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.shockwaveRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Neon glow effect using shadow blur
    ctx.shadowColor = '#ffaa00';
    ctx.shadowBlur = this.radius * 0.4;

    // Main explosion gradient: white core → orange → red → transparent
    const gradient = ctx.createRadialGradient(
      this.x, this.y, 0,
      this.x, this.y, this.radius
    );
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
    gradient.addColorStop(0.2, 'rgba(255, 220, 100, 0.85)');
    gradient.addColorStop(0.4, 'rgba(255, 170, 0, 0.7)');
    gradient.addColorStop(0.7, 'rgba(255, 68, 0, 0.4)');
    gradient.addColorStop(1, 'rgba(255, 68, 0, 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    ctx.fill();

    // Inner bright core for neon pop
    const coreRadius = this.radius * 0.25;
    if (coreRadius > 1) {
      ctx.shadowBlur = coreRadius * 2;
      ctx.shadowColor = '#ffffff';
      const coreGradient = ctx.createRadialGradient(
        this.x, this.y, 0,
        this.x, this.y, coreRadius
      );
      coreGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
      coreGradient.addColorStop(0.5, 'rgba(255, 255, 200, 0.5)');
      coreGradient.addColorStop(1, 'rgba(255, 220, 100, 0)');
      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(this.x, this.y, coreRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Check if a point is within the explosion radius (for collision detection).
   */
  containsPoint(px, py) {
    if (!this.alive || this.radius <= 0) return false;
    const dx = px - this.x;
    const dy = py - this.y;
    return (dx * dx + dy * dy) <= (this.radius * this.radius);
  }
}
